import { useEffect, useRef, useState } from 'react';

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
        autoPlay
        muted
        loop
        playsInline
        onCanPlay={() => setVideoLoaded(true)}
        poster="https://public.readdy.ai/ai/img_res/938521f5a0d92123c1f39cad2e1f7fa6.jpg"
      >
        <source
          src="https://videos.pexels.com/video-files/3252011/3252011-uhd_2560_1440_25fps.mp4"
          type="video/mp4"
        />
        <source
          src="https://videos.pexels.com/video-files/5989895/5989895-uhd_2560_1440_25fps.mp4"
          type="video/mp4"
        />
      </video>

      {/* Fallback poster when video not loaded */}
      {!videoLoaded && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=HR%20manager%20interviewing%20candidate%20in%20modern%20office%2C%20recruitment%20process%2C%20professional%20handshake%2C%20corporate%20environment%2C%20warm%20natural%20lighting%2C%20high%20quality%20photography&width=1920&height=1080&seq=hrposter2&orientation=landscape')`
          }}
        />
      )}

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>

      {/* Decorative color accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: '#B8860B' }}></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-32 pb-20 text-center">

        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 border border-white/20 backdrop-blur-sm"
          style={{ background: 'rgba(184,134,11,0.25)' }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#B8860B' }}></span>
          <span className="text-sm font-medium text-white/90 tracking-wide uppercase">PM Resources — Roma</span>
        </div>

        {/* Heading */}
        <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
          Soluzioni HR<br />
          <span style={{ color: '#B8860B' }}>Su Misura</span> per<br />
          Il Tuo Business
        </h1>

        {/* Subtitle */}
        <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed">
          Esternalizzazione del personale e ricerca professionale.<br />
          Il partner strategico che fa crescere la tua azienda.
        </p>

        {/* CTA Buttons */}
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <button
            onClick={() => scrollToSection('servizi')}
            className="inline-flex items-center gap-3 px-8 py-4 text-white text-base font-semibold rounded-full hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer group shadow-lg"
            style={{ background: '#1E3A5F' }}
          >
            Scopri i Nostri Servizi
            <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
          </button>
          <button
            onClick={() => scrollToSection('contatti')}
            className="inline-flex items-center gap-3 px-8 py-4 text-white text-base font-semibold rounded-full border-2 border-white/40 hover:border-white hover:bg-white/10 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            Contattaci
            <i className="ri-phone-line text-xl"></i>
          </button>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          {[
            { value: '500+', label: 'Professionisti Inseriti' },
            { value: '15+', label: 'Anni di Esperienza' },
            { value: '200+', label: 'Aziende Partner' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl font-bold text-white mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                {stat.value}
              </div>
              <div className="text-sm text-white/60 uppercase tracking-wide">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce cursor-pointer"
        onClick={() => scrollToSection('servizi')}>
        <span className="text-white/50 text-xs uppercase tracking-widest">Scorri</span>
        <i className="ri-arrow-down-line text-white/50 text-xl"></i>
      </div>
    </section>
  );
}
